import { useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import BreathOrb from "./BreathOrb";
import { TECH_BY_KEY, POSTURE_LABEL, intensityLabelKey } from "./breathTechniques";
import { ChevronLeft, Play, Check, Loader2 } from "lucide-react";

const WB = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function LibrarySession() {
  const { key } = useParams();
  const t = useT();
  const navigate = useNavigate();
  const tech = useMemo(() => TECH_BY_KEY[key], [key]);
  usePageMeta(tech ? t(`tech_${tech.key}`) : t("technique_not_found"));

  const [step, setStep] = useState(0); // 0 desc, 1 player, 2 wellbeing, 3 note, 4 success
  const [wellbeing, setWellbeing] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const runResult = useRef({ completed: false, elapsed_seconds: 0 });

  if (!tech) {
    return (
      <div className="py-16 text-center">
        <p className="text-sm text-muted-foreground">{t("technique_not_found")}</p>
        <Button variant="outline" onClick={() => navigate("/app/breathwork/techniques")} className="mt-4 rounded-full">{t("breathwork_techniques_title")}</Button>
      </div>
    );
  }

  const stepLines = () => {
    const out = [];
    for (let i = 1; i <= tech.stepCount; i++) {
      const k = `lib_tech_${tech.key}_step${i}`;
      const v = t(k);
      if (v && v !== k) out.push(v);
    }
    return out;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/library-sessions", {
        technique_key: tech.key, technique_id: tech.techniqueId,
        wellbeing_after: wellbeing, note: note.trim(),
        duration_seconds: runResult.current.elapsed_seconds, completed: runResult.current.completed,
      });
    } catch { /* silent */ } finally { setSaving(false); setStep(4); }
  };

  if (step === 1) {
    return (
      <div className="mx-auto max-w-xl" data-testid="lib-session-player">
        <BreathOrb phases={tech.phases} rounds={tech.rounds} techniqueLabel={t(`tech_${tech.key}`)}
          onComplete={(r) => { runResult.current = r; setStep(2); }}
          onCancel={() => { runResult.current = { completed: false, elapsed_seconds: 0 }; setStep(2); }} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-5" data-testid="lib-session-screen">
      <button onClick={() => (step === 0 ? navigate("/app/breathwork/techniques") : setStep(step - 1))} data-testid="lib-session-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t(`tech_${tech.key}`)}
      </button>

      {step === 0 ? (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">{tech.duration}</span>
            <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">{t(intensityLabelKey(tech.intensity))}</span>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">{(() => { const b = t(`lib_tech_${tech.key}_body`); return b && b !== `lib_tech_${tech.key}_body` ? b : t(`tech_${tech.key}_desc`); })()}</p>
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 text-center">
            <p className="text-xs text-muted-foreground">{t(POSTURE_LABEL[tech.posture])}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-primary">{t("explanation_how_it_works")}</p>
            {stepLines().map((line, idx) => {
              const isSafety = tech.safetyStepIndex && idx + 1 === tech.safetyStepIndex;
              return (
                <div key={idx} className={`flex items-start gap-2.5 py-1.5 ${isSafety ? "rounded-lg bg-rose-50 px-2" : ""}`}>
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${isSafety ? "bg-rose-600 text-white" : "bg-primary/15 text-primary"}`}>{idx + 1}</span>
                  <p className={`text-sm ${isSafety ? "font-medium text-rose-700" : "text-foreground"}`}>{line}</p>
                </div>
              );
            })}
          </div>
          <Button onClick={() => setStep(1)} data-testid="lib-session-start" className="w-full gap-2 rounded-full">
            <Play className="h-4 w-4" /> {t("lib_session_start_btn")}
          </Button>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-4">
          <div>
            <h1 className="font-display text-xl text-foreground">{t("lib_session_wellbeing_title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("lib_session_wellbeing_sub")}</p>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {WB.map((n) => (
              <button key={n} onClick={() => setWellbeing(n)} data-testid={`lib-wb-${n}`}
                className={`rounded-xl border py-3 text-base font-semibold transition-colors ${wellbeing === n ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>{n}</button>
            ))}
          </div>
          <Button onClick={() => setStep(3)} disabled={wellbeing === null} data-testid="lib-wb-continue" className="w-full rounded-full">{t("continue")}</Button>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="space-y-4">
          <div>
            <h1 className="font-display text-xl text-foreground">{t("lib_session_note_title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t("lib_session_note_sub")}</p>
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("lib_session_note_ph")} maxLength={2000} className="min-h-[120px]" data-testid="lib-note-input" />
          <Button onClick={save} disabled={saving} data-testid="lib-note-continue" className="w-full rounded-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("session_done_btn")}
          </Button>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary"><Check className="h-10 w-10 text-primary-foreground" /></span>
          <h1 className="font-display text-xl text-foreground">{t("session_success_title")}</h1>
          <p className="max-w-sm text-sm text-muted-foreground">{t("session_success_body")}</p>
          <Button onClick={() => navigate("/app/breathwork/techniques")} data-testid="lib-session-done" className="rounded-full">{t("lib_session_back_btn")}</Button>
        </div>
      ) : null}
    </div>
  );
}
